import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';
import { logger } from '../utils/logger';
import { getChallengeToken, documentTokenFromUrl } from './portalChallenge';

/**
 * Configured axios instances for routes NOT under `/api/admin`.
 *
 * Background: many modules historically called bare `axios.get('/api/...')`.
 * `setupAxiosAuth` patched the DEFAULT axios so those got the admin JWT, but they
 * still: (a) miss the 401→refresh response logic, and (b) hand-rolled their base
 * URL with inconsistent `REACT_APP_API_URL` fallbacks (some `:3001`, some
 * `:3001/api`), which double-`/api` in prod. These instances centralize that.
 *
 * Base URL truth: `REACT_APP_API_URL` is `<origin>/api` (e.g. https://cnfauto.com/api).
 * So baseURL below = `<origin>/api`, and callers pass paths WITHOUT the leading
 * `/api` (e.g. `apiRoot.get('/users')` → `<origin>/api/users`).
 *
 * - `api` (services/api.ts) → baseURL `<origin>/api/admin`  (admin endpoints)
 * - `apiRoot`              → baseURL `<origin>/api`         (non-admin, admin-authed)
 * - `customerPortalApi`    → baseURL `<origin>/api`         (customer token)
 * - `publicApi`            → baseURL `<origin>/api`         (no auth)
 */

if (!process.env.REACT_APP_API_URL) {
  logger.critical('REACT_APP_API_URL environment variable is not set!');
  throw new Error('Missing required environment variable: REACT_APP_API_URL');
}

const ROOT_BASE_URL = process.env.REACT_APP_API_URL; // <origin>/api

// ─── apiRoot: non-admin endpoints under /api, authenticated with the ADMIN JWT ──
export const apiRoot = axios.create({ baseURL: ROOT_BASE_URL, timeout: 10000 });

apiRoot.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Mirror the 401→refresh→retry behavior of the `api` instance so non-admin
// callers no longer hard-fail on token expiry.
apiRoot.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`,
            { refreshToken: tokens.refreshToken }
          );
          const { tokens: newTokens } = response.data;
          saveTokens(newTokens);
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return apiRoot(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ─── customerPortalApi: customer-portal / customer-auth routes (CUSTOMER token) ──
// Customer auth has its own refresh flow in CustomerAuthContext; this instance
// only attaches the token (parity with the previous hand-rolled bare calls — no
// auto-refresh here, by design).
const CUSTOMER_TOKEN_KEY = 'customer_access_token';

export const customerPortalApi = axios.create({ baseURL: ROOT_BASE_URL, timeout: 10000 });

customerPortalApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── publicApi: public routes (/public/*, public lookups) ─────────────────────
export const publicApi = axios.create({ baseURL: ROOT_BASE_URL, timeout: 10000 });

/**
 * WP-04 — attach whatever proves the caller may see this document.
 *
 * `/public/estimates/:token` and `/public/work-orders/:token` are no longer
 * open: the backend now requires the owning customer's login, an owner/advisor
 * staff token, or a passed vehicle challenge bound to that one document. This
 * interceptor supplies the two the browser can hold, so the change is invisible
 * to a customer who is already signed in.
 *
 * It is done here rather than at the call sites on purpose. Every public
 * document route needs the same header, and a per-call-site convention is one
 * forgotten line away from a route that "mysteriously" 401s — which is the same
 * class of mistake the audit found in `quote-requests.ts`, where three handlers
 * had the guard and five did not.
 *
 * The challenge token is looked up FROM THE URL, so it is always the session for
 * the document actually being requested. There is no ambient "current
 * document", and a session for one document is never sent to another — the
 * server would refuse it anyway, but sending it would produce a confusing 401
 * instead of an honest prompt.
 *
 * `Authorization` is only set when the caller has not already set it, so an
 * explicit token at a call site still wins.
 */
publicApi.interceptors.request.use(
  (config) => {
    const customerToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (customerToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${customerToken}`;
    }
    const documentToken = documentTokenFromUrl(config.url);
    const challenge = getChallengeToken(documentToken);
    if (challenge) {
      config.headers['X-Portal-Challenge'] = challenge;
    }
    return config;
  },
  (error) => Promise.reject(error)
);
